import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EllipsisOutlined,
  FilterOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { MenuProps, TableProps } from 'antd';
import { Button, Dropdown, Radio, Rate, Select, Table, Tag } from 'antd';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import { useLazyGetCustomersQuery } from '@/modules/customers/customersApi';
import { useLazyGetProductsQuery } from '@/modules/products/productsApi';
import {
  useBulkDeleteReviewsMutation,
  useBulkModerateReviewsMutation,
  useDeleteReviewMutation,
  useGetReviewsQuery,
  useModerateReviewMutation,
} from '@/modules/reviews/reviewsApi';
import type { Review, ReviewStatus, ReviewStatusFilter } from '@/types/review';
import { REVIEW_RATING_OPTIONS, REVIEW_STATUS_OPTIONS } from '@/types/review';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmBulkDelete, confirmDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import { ucfirst } from '@/utils/string';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { SortableColumnTitle } from '../categories/components/SortableColumnTitle';
import { useReviewQueryParams } from './hooks/useReviewQueryParams';
import styles from './Reviews.module.scss';

function statusTag(status: ReviewStatus) {
  if (status === 'approved') return <Tag color="success">Approved</Tag>;
  if (status === 'rejected') return <Tag color="error">Rejected</Tag>;
  return <Tag color="gold">Pending</Tag>;
}

export default function Reviews() {
  const notify = useNotify();
  const { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort } =
    useReviewQueryParams();
  const { data: listResult, isLoading, isFetching } = useGetReviewsQuery(apiParams);
  useGlobalLoader(isFetching && !!listResult);

  const reviews = useMemo(() => listResult?.results ?? [], [listResult?.results]);
  const activeSortBy = listResult?.sortBy ?? state.sortBy;
  const activeSortOrder = listResult?.sortOrder ?? state.sortOrder;
  const total = listResult?.total ?? 0;
  const currentPage = Number(listResult?.page ?? state.page);
  const pageSize = Number(listResult?.limit ?? state.limit);

  const [productDraft, setProductDraft] = useState<string | null>(state.productId || null);
  const [customerDraft, setCustomerDraft] = useState<string | null>(state.customerId || null);
  const [statusDraft, setStatusDraft] = useState<ReviewStatusFilter>(state.status);
  const [ratingDraft, setRatingDraft] = useState<number | 'all'>(state.rating);

  const [fetchProducts, { data: productsResult, isFetching: productsLoading }] =
    useLazyGetProductsQuery();
  const [fetchCustomers, { data: customersResult, isFetching: customersLoading }] =
    useLazyGetCustomersQuery();

  const [moderateReview] = useModerateReviewMutation();
  const [deleteReview] = useDeleteReviewMutation();
  const [bulkModerate, { isLoading: isBulkModerating }] = useBulkModerateReviewsMutation();
  const [bulkDelete, { isLoading: isBulkDeleting }] = useBulkDeleteReviewsMutation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const productFilterOptions = useMemo(() => {
    const products = productsResult?.results ?? [];
    const options = products.map((product) => ({
      label: product.sku ? `${ucfirst(product.name)} (${product.sku})` : ucfirst(product.name),
      value: product.id,
    }));

    if (state.productId && !options.some((option) => option.value === state.productId)) {
      const review = reviews.find((item) => item.productId === state.productId);
      if (review) {
        options.unshift({
          label: review.productSku
            ? `${ucfirst(review.productName)} (${review.productSku})`
            : ucfirst(review.productName),
          value: state.productId,
        });
      }
    }

    return options;
  }, [productsResult?.results, reviews, state.productId]);

  const customerFilterOptions = useMemo(() => {
    const customers = customersResult?.results ?? [];
    const options = customers.map((customer) => ({
      label: `${customer.name} (${customer.email})`,
      value: customer.id,
    }));

    if (state.customerId && !options.some((option) => option.value === state.customerId)) {
      const review = reviews.find((item) => item.customerId === state.customerId);
      if (review) {
        options.unshift({
          label: review.reviewerEmail
            ? `${review.reviewerName} (${review.reviewerEmail})`
            : review.reviewerName,
          value: state.customerId,
        });
      }
    }

    return options;
  }, [customersResult?.results, reviews, state.customerId]);

  const loadProductFilterOptions = useCallback(() => {
    void fetchProducts({
      page: 1,
      limit: 100,
      sortBy: 'name',
      sortOrder: 'asc',
      status: 'active',
    });
  }, [fetchProducts]);

  const loadCustomerFilterOptions = useCallback(() => {
    void fetchCustomers({
      page: 1,
      limit: 100,
      sortBy: 'name',
      sortOrder: 'asc',
      status: 'active',
    });
  }, [fetchCustomers]);

  const handleSort = useCallback(
    (field: string) => {
      const nextOrder: 'asc' | 'desc' =
        state.sortBy === field ? (state.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
      patchParams({ sortBy: field, sortOrder: nextOrder, page: 1 });
    },
    [patchParams, state.sortBy, state.sortOrder],
  );

  const handleModerate = useCallback(
    async (review: Review, status: Exclude<ReviewStatus, 'pending'>) => {
      const result = await resolveMutationResult(
        moderateReview({ id: review.id, body: { status } }),
      );

      if (result.ok) {
        notify.success(result.message ?? `Review ${status}.`);
        return;
      }

      notify.error(result.message);
    },
    [moderateReview, notify],
  );

  const handleDelete = useCallback(
    async (review: Review) => {
      const confirmed = await confirmDelete(`review by ${review.reviewerName}`);
      if (!confirmed) return;

      const result = await resolveMutationResult(deleteReview(review.id));
      if (result.ok) {
        notify.success(result.message ?? 'Review deleted.');
        setSelectedRowKeys((keys) => keys.filter((key) => key !== review.id));
        return;
      }

      notify.error(result.message);
    },
    [deleteReview, notify],
  );

  const handleBulkModerate = async (status: Exclude<ReviewStatus, 'pending'>) => {
    if (selectedRowKeys.length === 0) return;

    const result = await resolveMutationResult(bulkModerate({ ids: selectedRowKeys, status }));

    if (result.ok) {
      notify.success(result.message ?? `Reviews ${status}.`);
      setSelectedRowKeys([]);
      return;
    }

    notify.error(result.message);
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;

    const confirmed = await confirmBulkDelete(selectedRowKeys.length);
    if (!confirmed) return;

    const result = await resolveMutationResult(bulkDelete({ ids: selectedRowKeys }));

    if (result.ok) {
      notify.success(result.message ?? 'Reviews deleted.');
      setSelectedRowKeys([]);
      return;
    }

    notify.error(result.message);
  };

  const handleTableChange: TableProps<Review>['onChange'] = (pagination) => {
    const pageChanged = pagination.current !== undefined && pagination.current !== state.page;
    const limitChanged = pagination.pageSize !== undefined && pagination.pageSize !== state.limit;

    if (!pageChanged && !limitChanged) return;

    patchParams({
      ...(pageChanged && pagination.current !== undefined ? { page: pagination.current } : {}),
      ...(limitChanged && pagination.pageSize !== undefined ? { limit: pagination.pageSize } : {}),
    });
  };

  const getRowActionItems = useCallback(
    (review: Review): MenuProps['items'] => {
      const items: MenuProps['items'] = [];

      if (review.status !== 'approved') {
        items.push({
          key: 'approve',
          label: 'Approve',
          onClick: () => handleModerate(review, 'approved'),
        });
      }

      if (review.status !== 'rejected') {
        items.push({
          key: 'reject',
          label: 'Reject',
          danger: review.status === 'pending',
          onClick: () => handleModerate(review, 'rejected'),
        });
      }

      if (items.length) {
        items.push({ type: 'divider' });
      }

      items.push({
        key: 'delete',
        label: 'Delete',
        danger: true,
        onClick: () => handleDelete(review),
      });

      return items;
    },
    [handleDelete, handleModerate],
  );

  const columns: ColumnsType<Review> = useMemo(
    () => [
      {
        title: 'PRODUCT',
        key: 'product',
        className: styles.productCol,
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.productId ? [state.productId] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ productId: productDraft }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setProductDraft(null);
              patchParams({ productId: null }, { resetPage: true });
              close();
            }}
          >
            <Select
              allowClear
              showSearch
              size="small"
              placeholder="All products"
              value={productDraft ?? undefined}
              onChange={(value) => setProductDraft(value ?? null)}
              loading={productsLoading}
              options={productFilterOptions}
              optionFilterProp="label"
              style={{ width: '100%' }}
              onOpenChange={(visible) => {
                if (visible) loadProductFilterOptions();
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) {
              setProductDraft(state.productId || null);
              loadProductFilterOptions();
            }
          },
        },
        render: (_value, record) => (
          <div className={styles.productCell}>
            <Link to={`/products/${record.productId}/view`} className={styles.productLink}>
              {ucfirst(record.productName)}
            </Link>
            {record.productSku ? (
              <span className={styles.productSku}>{record.productSku}</span>
            ) : null}
          </div>
        ),
      },
      {
        title: 'CUSTOMER',
        key: 'customer',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.customerId ? [state.customerId] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ customerId: customerDraft }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setCustomerDraft(null);
              patchParams({ customerId: null }, { resetPage: true });
              close();
            }}
          >
            <Select
              allowClear
              showSearch
              size="small"
              placeholder="All customers"
              value={customerDraft ?? undefined}
              onChange={(value) => setCustomerDraft(value ?? null)}
              loading={customersLoading}
              options={customerFilterOptions}
              optionFilterProp="label"
              style={{ width: '100%' }}
              onOpenChange={(visible) => {
                if (visible) loadCustomerFilterOptions();
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) {
              setCustomerDraft(state.customerId || null);
              loadCustomerFilterOptions();
            }
          },
        },
        render: (_value, record) => (
          <div className={styles.reviewerCell}>
            <span className={styles.reviewerName}>{record.reviewerName}</span>
            {record.reviewerEmail ? (
              <span className={styles.reviewerEmail}>{record.reviewerEmail}</span>
            ) : null}
          </div>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="RATING"
            field="rating"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'rating',
        key: 'rating',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.rating !== 'all' ? [String(state.rating)] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                { rating: ratingDraft === 'all' ? null : ratingDraft },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setRatingDraft('all');
              patchParams({ rating: null }, { resetPage: true });
              close();
            }}
          >
            <Select
              size="small"
              value={ratingDraft}
              onChange={setRatingDraft}
              style={{ width: '100%' }}
              options={[{ label: 'All ratings', value: 'all' }, ...REVIEW_RATING_OPTIONS]}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setRatingDraft(state.rating);
          },
        },
        render: (rating: number) => (
          <Rate disabled allowHalf={false} value={rating} className={styles.ratingStars} />
        ),
      },
      {
        title: 'COMMENT',
        dataIndex: 'comment',
        key: 'comment',
        className: styles.commentCol,
        render: (comment: string) => (
          <span className={styles.commentCell} title={comment}>
            {comment}
          </span>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="STATUS"
            field="status"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.status !== 'all' ? [state.status] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                { status: statusDraft === 'all' ? null : statusDraft },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setStatusDraft('all');
              patchParams({ status: null }, { resetPage: true });
              close();
            }}
          >
            <Radio.Group
              value={statusDraft}
              onChange={(event) => setStatusDraft(event.target.value)}
              className={styles.statusRadio}
            >
              <Radio value="all">All</Radio>
              {REVIEW_STATUS_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setStatusDraft(state.status);
          },
        },
        render: (status: ReviewStatus) => statusTag(status),
      },
      {
        title: (
          <SortableColumnTitle
            label="SUBMITTED"
            field="createdAt"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value: string) => <span className={styles.dateCell}>{formatDateTime(value)}</span>,
      },
      {
        title: 'ACTION',
        key: 'actions',
        align: 'center',
        render: (_value, record) => (
          <Dropdown menu={{ items: getRowActionItems(record) }} trigger={['click']}>
            <Button
              type="text"
              size="small"
              icon={<EllipsisOutlined />}
              className={styles.actionBtn}
            />
          </Dropdown>
        ),
      },
    ],
    [
      activeSortBy,
      activeSortOrder,
      customerDraft,
      customerFilterOptions,
      customersLoading,
      getRowActionItems,
      handleSort,
      loadCustomerFilterOptions,
      loadProductFilterOptions,
      patchParams,
      productDraft,
      productFilterOptions,
      productsLoading,
      ratingDraft,
      state.customerId,
      state.productId,
      state.rating,
      state.status,
      statusDraft,
    ],
  );

  const rowSelection: TableRowSelection<Review> = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys as string[]),
  };

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          hasActiveFiltersOrSort ? (
            <Button
              type="default"
              size="small"
              className={styles.headerBtn}
              icon={<ReloadOutlined />}
              onClick={() => {
                setProductDraft(null);
                setCustomerDraft(null);
                setStatusDraft('all');
                setRatingDraft('all');
                resetAll();
              }}
            >
              Reset
            </Button>
          ) : null
        }
      />

      {selectedRowKeys.length > 0 ? (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedRowKeys.length} selected</span>
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            loading={isBulkModerating}
            onClick={() => handleBulkModerate('approved')}
          >
            Approve
          </Button>
          <Button
            size="small"
            icon={<CloseCircleOutlined />}
            loading={isBulkModerating}
            onClick={() => handleBulkModerate('rejected')}
          >
            Reject
          </Button>
          <Button size="small" danger loading={isBulkDeleting} onClick={handleBulkDelete}>
            Delete
          </Button>
          <Button size="small" type="link" onClick={() => setSelectedRowKeys([])}>
            Clear
          </Button>
        </div>
      ) : null}

      <div className={styles.tableCard}>
        <Table<Review>
          className={styles.table}
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={reviews}
          loading={isLoading && !listResult}
          rowSelection={rowSelection}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (value) => `${value} reviews`,
            size: 'small',
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
}
